import { EntitlementCollection, entitlements } from "./entitlements";

export enum SubscriptionType
{
    Free,
    Paid,
    PaidWithFreeTrial,
}

export interface Plan
{
    id: string,
    bundle: string,
    type: SubscriptionType,
    entitlements: EntitlementCollection,
    upgradable?: string[],
    downgradable?: string[]
}

export const PLANS = {
    natureRenderer: {
        free: {
            staging: "zMuR9VBTMx",
            production: "Dk8TqOQzIM"
        },
        plus: {
            monthly: {
                staging: "816640",
                production: "816634"
            },
            annual: {
                staging: "816641",
                production: "816635"
            }
        },
        pro: {
            monthly: {
                staging: "816642",
                production: "816637"
            },
            annual: {
                staging: "816643",
                production: "816638"
            }
        },
        legacy: {
            indie: {
                monthly: {
                    staging: "803804",
                    production: "804753"
                },
                annual: {
                    staging: "803802",
                    production: "804754"
                }
            },
            pro: {
                monthly: {
                    staging: "803805",
                    production: "804755"
                },
                annual: {
                    staging: "803801",
                    production: "804756"
                }
            },
            $8: {
                trial14Days: {
                    staging: "787859",
                    production: "742917"
                },
                trial90Days: {
                    staging: "787161",
                    production: "760890"
                }
            },
            $6: {
                trial14Days: {
                    staging: "752903",
                    production: "770898"
                },
                trial90Days: {
                    staging: "787861",
                    production: "770899"
                },
                customTrial14Days: {
                    staging: "793386",
                    production: "787869"
                },
                customTrial90Days: {
                    staging: "",
                    production: "787870"
                },
                noTrial: {
                    monthly: {
                        staging: "",
                        production: "793387"
                    },
                    annual: {
                        staging: "798039",
                        production: "798040"
                    }
                }
            }
        }
    }
};

const natureRendererAccess = [
    entitlements.natureRenderer.software.access
];

const natureRendererPC = [
    entitlements.natureRenderer.platforms.windows,
    entitlements.natureRenderer.platforms.mac,
    entitlements.natureRenderer.platforms.linux
];

const natureRendererMobile = [
    entitlements.natureRenderer.platforms.android,
    entitlements.natureRenderer.platforms.iOS
];

const natureRendererConsole = [
    entitlements.natureRenderer.platforms.xbox,
    entitlements.natureRenderer.platforms.playStation,
    entitlements.natureRenderer.platforms.switch
];

const natureRendererAllAccess: EntitlementCollection = [
    ...natureRendererAccess,
    ...natureRendererPC,
    ...natureRendererMobile,
    ...natureRendererConsole
];

type PlanData = Omit<Plan, "id">;

const planData: Record<string, PlanData> = {
    // Staging new (Free, Plus, Pro)
    [PLANS.natureRenderer.free.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Free,
        entitlements: [...natureRendererAccess, ...natureRendererPC],
        upgradable: [
            PLANS.natureRenderer.plus.monthly.staging,
            PLANS.natureRenderer.plus.annual.staging,
            PLANS.natureRenderer.pro.monthly.staging,
            PLANS.natureRenderer.pro.annual.staging
        ]
    },
    [PLANS.natureRenderer.plus.monthly.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: [...natureRendererAccess, ...natureRendererPC, ...natureRendererMobile],
        upgradable: [
            PLANS.natureRenderer.pro.monthly.staging
        ]
    },
    [PLANS.natureRenderer.plus.annual.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: [...natureRendererAccess, ...natureRendererPC, ...natureRendererMobile],
        upgradable: [
            PLANS.natureRenderer.pro.annual.staging
        ]
    },
    [PLANS.natureRenderer.pro.monthly.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: [...natureRendererAccess, ...natureRendererPC, ...natureRendererMobile, ...natureRendererConsole],
        downgradable: [
            PLANS.natureRenderer.plus.monthly.staging
        ]
    },
    [PLANS.natureRenderer.pro.annual.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: [...natureRendererAccess, ...natureRendererPC, ...natureRendererMobile, ...natureRendererConsole],
        downgradable: [
            PLANS.natureRenderer.plus.annual.staging
        ]
    },

    // Production (Free, Plus, Pro)
    [PLANS.natureRenderer.free.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Free,
        entitlements: [...natureRendererAccess, ...natureRendererPC],
        upgradable: [
            PLANS.natureRenderer.plus.monthly.production,
            PLANS.natureRenderer.plus.annual.production,
            PLANS.natureRenderer.pro.monthly.production,
            PLANS.natureRenderer.pro.annual.production
        ]
    },
    [PLANS.natureRenderer.plus.monthly.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: [...natureRendererAccess, ...natureRendererPC, ...natureRendererMobile],
        upgradable: [
            PLANS.natureRenderer.pro.monthly.production
        ]
    },
    [PLANS.natureRenderer.plus.annual.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: [...natureRendererAccess, ...natureRendererPC, ...natureRendererMobile],
        upgradable: [
            PLANS.natureRenderer.pro.annual.production
        ]
    },
    [PLANS.natureRenderer.pro.monthly.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: [...natureRendererAccess, ...natureRendererPC, ...natureRendererMobile, ...natureRendererConsole],
        downgradable: [
            PLANS.natureRenderer.plus.monthly.production
        ]
    },
    [PLANS.natureRenderer.pro.annual.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: [...natureRendererAccess, ...natureRendererPC, ...natureRendererMobile, ...natureRendererConsole],
        downgradable: [
            PLANS.natureRenderer.plus.annual.production
        ]
    },

    // Legacy Staging (Indie, Pro, Enterprise)
    [PLANS.natureRenderer.legacy.pro.monthly.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.indie.monthly.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.indie.annual.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.pro.annual.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },

    // Legacy Staging
    [PLANS.natureRenderer.legacy.$8.trial14Days.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$8.trial90Days.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.trial14Days.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.customTrial14Days.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.noTrial.annual.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.trial90Days.staging]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },

    // Legacy Production (Indie, Pro, Enterprise)
    [PLANS.natureRenderer.legacy.pro.annual.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.pro.monthly.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.indie.annual.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.indie.monthly.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },

    // Legacy Production
    [PLANS.natureRenderer.legacy.$8.trial14Days.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$8.trial90Days.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.trial14Days.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.trial90Days.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.customTrial14Days.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.customTrial90Days.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.PaidWithFreeTrial,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.noTrial.monthly.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    },
    [PLANS.natureRenderer.legacy.$6.noTrial.annual.production]: {
        bundle: "com.vdc.nature-renderer",
        type: SubscriptionType.Paid,
        entitlements: natureRendererAllAccess
    }
};

export function getPlan (id: string): Plan | undefined
{
    const data = planData[id];
    if (!data)
        return undefined;

    return {
        id: id,
        ...data
    };
}

export function getPlansWithBundle (bundle: string): Plan[]
{
    const keys = Object.keys(planData);
    const result = new Array(keys.length);
    for (let i = 0; i < keys.length; i++)
    {
        result[i] = {
            id: keys[i],
            ...planData[keys[i]]
        };
    }

    return result;
}
